(function (root) {
  "use strict";
  if (root.JTAddressSelector) return;
  const cache = new Map();
  let sequence = 0;
  const storageKey = "jt-delivery-address:v1";
  // Shopify's current PH province menu still uses these older postal labels.
  const checkoutProvinces = {
    "Davao de Oro": "Compostela Valley",
    "Mountain Province": "Mountain",
    "Maguindanao del Norte": "Maguindanao",
    "Maguindanao del Sur": "Maguindanao",
  };

  function normalize(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function matches(item, query) {
    const words = normalize(query).split(" ").filter(Boolean);
    const haystack = normalize(`${item.name} ${item.aliases || ""}`);
    return words.every((word) => haystack.includes(word));
  }

  function shippingFields(address) {
    const barangay = /^(?:barangay|brgy\.?)(?:\s|$)/i.test(address.barangay)
      ? address.barangay
      : `Barangay ${address.barangay}`;
    const fields = {
      country: "Philippines",
      province:
        checkoutProvinces[address.shippingProvince || address.province] ||
        address.shippingProvince ||
        (address.specialArea ? "" : address.province),
      city: address.city,
      address1: `${address.street}, ${barangay}`,
      address2: address.landmark || "",
      zip: address.zip || "",
    };
    return fields;
  }

  function checkoutUrl(address) {
    if (!address) return "/checkout";
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(shippingFields(address))) {
      query.set(`checkout[shipping_address][${key}]`, value);
    }
    return `/checkout?${query}`;
  }

  function remember(address) {
    try {
      root.sessionStorage.setItem(
        storageKey,
        JSON.stringify({ at: Date.now(), address }),
      );
    } catch {
      /* private browsing */
    }
  }

  function recalled() {
    try {
      const value = JSON.parse(root.sessionStorage.getItem(storageKey));
      if (
        value?.at > Date.now() - 30 * 60 * 1000 &&
        value.at <= Date.now() &&
        value.address &&
        typeof value.address.street === "string"
      )
        return value.address;
      root.sessionStorage.removeItem(storageKey);
    } catch {
      /* no saved address */
    }
    return null;
  }

  async function loadJson(url) {
    if (!cache.has(url)) {
      cache.set(
        url,
        (async () => {
          const controller = new AbortController();
          const timeout = root.setTimeout(() => controller.abort(), 12000);
          try {
            const response = await fetch(url, {
              credentials: "omit",
              signal: controller.signal,
            });
            if (!response.ok) throw new Error("Address list unavailable");
            return await response.json();
          } finally {
            root.clearTimeout(timeout);
          }
        })(),
      );
    }
    try {
      return await cache.get(url);
    } catch (error) {
      cache.delete(url);
      throw error;
    }
  }

  function addStyles() {
    if (document.getElementById("jt-address-styles")) return;
    const style = document.createElement("style");
    style.id = "jt-address-styles";
    style.textContent = `
      .jt-address-form {font-family:var(--jt-gate-font,var(--font-body-family,inherit));font-size:1em;font-weight:var(--font-body-weight,400);line-height:1.5;color:inherit;letter-spacing:normal;}
      .jt-address-form * {box-sizing:border-box;font-family:inherit;}
      .jt-address-form [hidden] {display:none!important;}
      .jt-address-field {position:relative;margin:0 0 16px;}
      .jt-address-field label {display:block;font-size:.875em;font-weight:var(--font-body-weight,400);margin:0 0 7px;line-height:1.5;}
      .jt-address-field input {display:block;min-width:0;width:100%;height:48px;border:var(--inputs-border-width,1px) solid rgba(var(--jt-gate-ink,24,24,24),var(--inputs-border-opacity,.55));border-radius:var(--jt-gate-input-radius,0px);padding:12px 14px;font-size:1em;font-weight:var(--font-body-weight,400);line-height:1.4;letter-spacing:normal;color:inherit;background:rgb(var(--jt-gate-surface,250,249,248));box-shadow:none;}
      .jt-address-field input[role=combobox] {padding-right:38px;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='m1 1 5 5 5-5' fill='none' stroke='%23696969' stroke-width='1.25'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;}
      .jt-address-field input:disabled {background-color:rgba(var(--jt-gate-ink,24,24,24),.04);color:rgba(var(--jt-gate-ink,24,24,24),.6);cursor:not-allowed;}
      .jt-address-field input:hover:not(:disabled) {border-color:rgba(var(--jt-gate-ink,24,24,24),.85);}
      .jt-address-form input[aria-invalid=true] {border-color:oklch(47% .17 27);}
      .jt-address-field small {display:block;margin-top:7px;font-size:.8125em;line-height:1.55;color:rgba(var(--jt-gate-ink,24,24,24),.75);}
      .jt-address-options {position:absolute;top:100%;left:0;right:0;z-index:2;background:rgb(var(--jt-gate-surface,250,249,248));border:1px solid rgba(var(--jt-gate-ink,24,24,24),.55);border-radius:var(--jt-gate-input-radius,0px);box-shadow:0 8px 24px rgba(var(--jt-gate-ink,24,24,24),.12);padding:4px 0;max-height:240px;overflow:auto;list-style:none;margin:4px 0 0;}
      .jt-address-options li {padding:11px 14px;font-size:.875em;line-height:1.5;cursor:pointer;}
      .jt-address-options li[aria-selected=true],.jt-address-options li:hover {background:rgba(var(--jt-gate-ink,24,24,24),.06);color:inherit;}
      .jt-address-options .jt-address-no-options {cursor:default;color:rgba(var(--jt-gate-ink,24,24,24),.75);font-size:.8125em;}
      .jt-address-form button.jt-address-link {display:inline-block;min-height:36px;border:0;border-radius:0;padding:4px 0;margin:0 0 16px;background:none;color:inherit;text-decoration:underline;text-underline-offset:.25em;font-size:.8125em;font-weight:var(--font-body-weight,400);line-height:1.6;cursor:pointer;}
      .jt-address-status {font-size:.8125em;line-height:1.6;color:rgba(var(--jt-gate-ink,24,24,24),.75);margin:0 0 16px;}
      .jt-address-preview {padding:14px 16px;border:1px solid rgba(var(--jt-gate-ink,24,24,24),.12);background:rgba(var(--jt-gate-ink,24,24,24),.025);border-radius:var(--jt-gate-input-radius,0px);margin:4px 0 16px;font-size:.875em;line-height:1.65;overflow-wrap:anywhere;}
      .jt-address-preview strong {display:block;font-weight:var(--font-body-weight-bold,600);margin-bottom:4px;}
      .jt-address-source {margin:0;color:rgba(var(--jt-gate-ink,24,24,24),.75);font-size:.75em;line-height:1.5;}
      .jt-address-source a {color:inherit;text-decoration:underline;text-underline-offset:.25em;}
    `;
    document.head.append(style);
  }

  function create(options) {
    addStyles();
    const prefix = `jt-address-${++sequence}`;
    const indexUrl = new URL(options.indexUrl, root.location.href).toString();
    const element = document.createElement("div");
    element.className = "jt-address-form";
    element.setAttribute("data-jt-address-selector", "1");
    let index = null;
    let manual = false;
    let disposed = false;
    let generation = 0;
    const fields = {};
    const status = document.createElement("p");
    status.className = "jt-address-status";
    status.setAttribute("role", "status");
    status.textContent = "Loading Philippine address options…";
    element.append(status);

    function combobox(key, text, placeholder) {
      const wrap = document.createElement("div");
      wrap.className = "jt-address-field";
      const label = document.createElement("label");
      const input = document.createElement("input");
      const list = document.createElement("ul");
      input.id = `${prefix}-${key}`;
      label.htmlFor = input.id;
      label.textContent = text;
      input.type = "text";
      input.autocomplete = "off";
      input.placeholder = placeholder;
      input.disabled = true;
      input.maxLength = 255;
      input.setAttribute("role", "combobox");
      input.setAttribute("aria-autocomplete", "list");
      input.setAttribute("aria-expanded", "false");
      input.setAttribute("aria-controls", `${input.id}-options`);
      input.setAttribute("aria-required", "true");
      list.id = `${input.id}-options`;
      list.className = "jt-address-options";
      list.setAttribute("role", "listbox");
      list.setAttribute("aria-label", text);
      list.hidden = true;
      let items = [];
      let selected = null;
      let active = -1;
      let visible = [];
      function hide() {
        list.hidden = true;
        input.setAttribute("aria-expanded", "false");
        input.removeAttribute("aria-activedescendant");
      }
      function choose(item) {
        selected = item;
        input.value = item.name;
        input.removeAttribute("aria-invalid");
        hide();
        changed(key);
      }
      function render() {
        if (manual || input.disabled) return;
        visible = items
          .filter((item) => matches(item, selected ? "" : input.value))
          .slice(0, 60);
        list.replaceChildren();
        active = -1;
        for (const [position, item] of visible.entries()) {
          const row = document.createElement("li");
          row.id = `${input.id}-option-${position}`;
          row.setAttribute("role", "option");
          row.setAttribute("aria-selected", "false");
          row.textContent = item.name;
          row.addEventListener("mousedown", (event) => event.preventDefault());
          row.addEventListener("click", () => {
            choose(item);
            input.focus();
            hide();
          });
          list.append(row);
        }
        if (
          !visible.length ||
          items.filter((item) => matches(item, selected ? "" : input.value))
            .length > 60
        ) {
          const row = document.createElement("li");
          row.className = "jt-address-no-options";
          row.textContent = visible.length
            ? "Type more letters to narrow the list."
            : "No match. Try another spelling or enter your address manually below.";
          list.append(row);
        }
        list.hidden = false;
        input.setAttribute("aria-expanded", "true");
      }
      input.addEventListener("focus", render);
      input.addEventListener("blur", hide);
      input.addEventListener("input", () => {
        selected = null;
        input.removeAttribute("aria-invalid");
        changed(key);
        render();
      });
      input.addEventListener("keydown", (event) => {
        if (manual) return;
        if (event.key === "Escape" && !list.hidden) {
          event.stopPropagation();
          hide();
          return;
        }
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (list.hidden) render();
          active = Math.max(
            0,
            Math.min(
              visible.length - 1,
              active + (event.key === "ArrowDown" ? 1 : -1),
            ),
          );
          Array.from(list.children).forEach((row, i) =>
            row.setAttribute("aria-selected", String(i === active)),
          );
          if (visible[active]) {
            input.setAttribute(
              "aria-activedescendant",
              `${input.id}-option-${active}`,
            );
            list.children[active].scrollIntoView({ block: "nearest" });
          }
        } else if (event.key === "Enter" && !list.hidden) {
          event.preventDefault();
          const exact = visible.find(
            (item) => normalize(item.name) === normalize(input.value),
          );
          if (visible[active] || exact || visible.length === 1)
            choose(visible[active] || exact || visible[0]);
        }
      });
      wrap.append(label, input, list);
      element.append(wrap);
      return {
        input,
        value: () => selected,
        setItems(values) {
          items = values;
          selected = null;
          input.value = "";
          hide();
          input.disabled = !values.length;
        },
        select: choose,
        close: hide,
        destroy() {
          hide();
        },
      };
    }

    fields.province = combobox(
      "province",
      "Province / Metro Manila",
      "Search or choose your province",
    );
    fields.city = combobox(
      "city",
      "City / Municipality",
      "Choose your province first",
    );
    fields.barangay = combobox(
      "barangay",
      "Barangay",
      "Choose your city first",
    );
    const manualButton = document.createElement("button");
    manualButton.type = "button";
    manualButton.className = "jt-address-link";
    manualButton.textContent = "Can’t find your address? Enter it manually";
    element.append(manualButton);

    function textField(key, labelText, placeholder, hint) {
      const wrap = document.createElement("div");
      wrap.className = "jt-address-field";
      const label = document.createElement("label");
      const input = document.createElement("input");
      input.id = `${prefix}-${key}`;
      input.type = "text";
      input.maxLength = 255;
      input.placeholder = placeholder;
      label.htmlFor = input.id;
      label.textContent = labelText;
      wrap.append(label, input);
      if (hint) {
        const help = document.createElement("small");
        help.id = `${input.id}-help`;
        help.textContent = hint;
        input.setAttribute("aria-describedby", help.id);
        wrap.append(help);
      }
      input.addEventListener("input", () => {
        input.removeAttribute("aria-invalid");
        updatePreview();
      });
      element.append(wrap);
      return { input };
    }
    fields.street = textField(
      "street",
      "House / building, street, purok or sitio",
      "e.g. Purok 2, near the barangay hall",
      "Walang house number? Ilagay ang purok, sitio o malinaw na direksyon.",
    );
    fields.street.input.setAttribute("aria-required", "true");
    fields.landmark = textField(
      "landmark",
      "Landmark / delivery directions (optional)",
      "e.g. Blue gate beside the chapel",
    );
    fields.zip = textField(
      "zip",
      "ZIP code (optional here)",
      "4 digits, if known",
      "At checkout, confirm your ZIP code and enter this same barangay in Shopify’s Barangay field.",
    );
    fields.zip.input.inputMode = "numeric";
    fields.zip.input.autocomplete = "postal-code";
    fields.zip.input.maxLength = 4;
    const preview = document.createElement("div");
    preview.className = "jt-address-preview";
    preview.hidden = true;
    const previewTitle = document.createElement("strong");
    previewTitle.textContent = "Your delivery address";
    const previewText = document.createElement("span");
    preview.append(previewTitle, previewText);
    const source = document.createElement("p");
    source.className = "jt-address-source";
    const sourceLink = document.createElement("a");
    sourceLink.href = "https://psa.gov.ph/classification/psgc";
    sourceLink.target = "_blank";
    sourceLink.rel = "noopener noreferrer";
    sourceLink.textContent =
      "Locality options: PSA PSGC, June 2026 (CC BY 4.0)";
    source.append(sourceLink);
    element.append(preview, source);

    function draft() {
      const province = fields.province.value();
      return {
        province: fields.province.input.value.trim(),
        city: fields.city.input.value.trim(),
        barangay: fields.barangay.input.value.trim(),
        street: fields.street.input.value.trim(),
        landmark: fields.landmark.input.value.trim(),
        zip: fields.zip.input.value.trim(),
        shippingProvince: manual
          ? fields.province.input.value.trim()
          : province?.shippingProvince || "",
        specialArea: !manual && province?.id === "1999900000",
        provinceId: manual ? null : province?.id || null,
        cityId: manual ? null : fields.city.value()?.id || null,
        barangayId: manual ? null : fields.barangay.value()?.id || null,
        manual,
        datasetVersion: index?.version || null,
      };
    }
    function updatePreview() {
      const value = draft();
      preview.hidden = !value.street || !value.city || !value.barangay;
      previewText.textContent = [
        value.street,
        value.barangay,
        value.city,
        value.province,
        value.zip,
        value.landmark,
      ]
        .filter(Boolean)
        .join(", ");
    }
    async function changed(key) {
      if (!manual && key === "province") {
        generation += 1;
        fields.city.setItems(fields.province.value()?.cities || []);
        fields.city.input.placeholder = "Search or choose your city";
        fields.barangay.setItems([]);
        fields.barangay.input.placeholder = "Choose your city first";
        status.textContent =
          fields.province.value()?.id === "1999900000"
            ? "For the Special Geographic Area, confirm the province shown in Shopify checkout."
            : "Choose the barangay where the rider should deliver.";
      } else if (!manual && key === "city") {
        const request = ++generation;
        fields.barangay.setItems([]);
        const city = fields.city.value();
        if (city && index) {
          fields.barangay.input.placeholder = "Loading barangays…";
          try {
            const url = new URL(
              index.regionFiles[city.region],
              indexUrl,
            ).toString();
            const chunk = await loadJson(url);
            if (disposed || generation !== request || manual) return;
            if (
              chunk.version !== index.version ||
              !Array.isArray(chunk.barangays?.[city.id])
            )
              throw new Error("Address list mismatch");
            fields.barangay.setItems(
              chunk.barangays[city.id].map(([id, name, aliases]) => ({
                id,
                name,
                aliases,
              })),
            );
            fields.barangay.input.placeholder =
              "Search or choose your barangay";
          } catch {
            if (disposed || generation !== request) return;
            setManual(
              true,
              "The barangay list couldn’t load. Please type your address instead; you can still continue.",
            );
          }
        }
      }
      updatePreview();
    }
    function setManual(value, message) {
      generation += 1;
      manual = value;
      for (const key of ["province", "city", "barangay"]) {
        fields[key].close();
        fields[key].input.disabled = false;
        fields[key].input.setAttribute("role", manual ? "textbox" : "combobox");
        fields[key].input.placeholder =
          `Type your ${key === "city" ? "city / municipality" : key}`;
      }
      manualButton.textContent = manual
        ? "Use the address options instead"
        : "Can’t find your address? Enter it manually";
      status.textContent =
        message ||
        (manual
          ? "Type your correct province, city and barangay. A missing list entry doesn’t mean your address is invalid."
          : "Choose the barangay where the rider should deliver.");
      if (!manual && index) {
        fields.province.setItems(index.provinces);
        fields.city.setItems([]);
        fields.barangay.setItems([]);
        fields.province.input.placeholder = "Search or choose your province";
        fields.city.input.placeholder = "Choose your province first";
        fields.barangay.input.placeholder = "Choose your city first";
      }
      updatePreview();
    }
    manualButton.addEventListener("click", async () => {
      if (manual && !index) {
        status.textContent = "Retrying the address list…";
        await initialize();
      } else setManual(!manual);
      fields.province.input.focus();
    });

    function read() {
      const value = draft();
      for (const key of ["province", "city", "barangay", "street"]) {
        if (
          !value[key] ||
          (!manual && fields[key].value && !fields[key].value())
        ) {
          fields[key].input.setAttribute("aria-invalid", "true");
          fields[key].input.focus();
          return {
            ok: false,
            message:
              key === "street"
                ? "Add your street, purok, sitio or clear delivery directions. A house number is not required."
                : `Please ${manual ? "enter" : "choose"} your ${key === "city" ? "city / municipality" : key}.`,
          };
        }
      }
      if (value.zip && !/^\d{4}$/.test(value.zip)) {
        fields.zip.input.setAttribute("aria-invalid", "true");
        fields.zip.input.focus();
        return {
          ok: false,
          message:
            "Use a 4-digit ZIP code, or leave it blank to confirm at checkout.",
        };
      }
      if (
        Object.values(shippingFields(value)).some((part) => part.length > 255)
      ) {
        fields.street.input.focus();
        return {
          ok: false,
          message:
            "Keep the street and barangay together under 255 characters. Put extra directions in Landmark.",
        };
      }
      remember(value);
      return { ok: true, address: value };
    }
    async function initialize() {
      try {
        index = await loadJson(indexUrl);
        if (disposed) return;
        if (
          !index.version ||
          !Array.isArray(index.provinces) ||
          !index.regionFiles
        )
          throw new Error("Address list mismatch");
        setManual(false);
        const previous = options.prefill || recalled();
        if (previous) {
          fields.street.input.value = previous.street || "";
          fields.landmark.input.value = previous.landmark || "";
          fields.zip.input.value = previous.zip || "";
          if (previous.manual) {
            setManual(true);
            for (const key of ["province", "city", "barangay"])
              fields[key].input.value = previous[key] || "";
          } else {
            const province = index.provinces.find(
              (item) => item.id === previous.provinceId,
            );
            if (province) {
              fields.province.select(province);
              const city = province.cities.find(
                (item) => item.id === previous.cityId,
              );
              if (city) {
                fields.city.select(city);
                await changed("city");
                // Select only an entry present in the freshly loaded parent list.
                const url = new URL(
                  index.regionFiles[city.region],
                  indexUrl,
                ).toString();
                const row = (await loadJson(url)).barangays[city.id]?.find(
                  ([id]) => id === previous.barangayId,
                );
                if (!manual && !disposed && row)
                  fields.barangay.select({
                    id: row[0],
                    name: row[1],
                    aliases: row[2],
                  });
              }
            }
          }
          updatePreview();
        }
      } catch {
        if (!disposed)
          setManual(
            true,
            "The address options couldn’t load. Please type your address instead; you can still continue.",
          );
      }
    }
    const ready = initialize();
    return {
      element,
      ready,
      read,
      focus() {
        fields.province.input.focus();
      },
      destroy() {
        disposed = true;
        generation += 1;
        Object.values(fields).forEach((field) => field.destroy?.());
      },
    };
  }
  root.JTAddressSelector = {
    create,
    checkoutUrl,
    shippingFields,
    normalize,
    matches,
  };
})(typeof window === "undefined" ? globalThis : window);
